/**
 * The merchant's packing-material catalogue.
 *
 * MIRRORS `freeze-v2/data/noberoFreeze.js`'s `PACKS` and the label maps in
 * `freeze-v2/packing/state/packingModel.js`, copied rather than imported
 * because the two modules are deliberately independent — the same rule every
 * other mirrored file in `disputes-v2/freeze/` follows.
 *
 * WHY IT IS HERE AT ALL: a weight freeze is granted against one declared
 * packing material and nothing else, so the drawer has to name which. Before
 * this file the field was free text, because nothing in the dispute dataset
 * records what a shipment was packed in. That is still true — no dispute row
 * carries a `packId`, and NOTHING here is pre-selected for the merchant. The
 * catalogue is real, the choice is the merchant's, and the card below the
 * picker shows the real specification of whatever they pick.
 *
 * DRIFT: this is a second copy of a nine-row dataset. `packs.test.js` pins the
 * ids and the figures so an edit to the freeze module's catalogue that never
 * reaches this one fails loudly instead of quietly showing a merchant a carton
 * that no longer exists.
 */

export const PACK_KIND = {FLYER: 'flyer', CARTON: 'carton', OTHER: 'other'}
export const PACK_BRANDING = {SELF: 'self', GENERIC: 'generic'}

/**
 * Short labels, not the freeze module's long ones ('Cardboard box or carton').
 * A picker option reads `Carton, medium (self-branded)`, and the card beside it
 * has a `Type` row two words wide; the long form wraps in both.
 */
export const PACK_KIND_LABEL = {
  [PACK_KIND.FLYER]: 'Flyer',
  [PACK_KIND.CARTON]: 'Carton',
  [PACK_KIND.OTHER]: 'Other',
}

export const PACK_BRANDING_LABEL = {
  [PACK_BRANDING.SELF]: 'Self-branded',
  [PACK_BRANDING.GENERIC]: 'Generic',
}

/**
 * `dims` is a [l, b, h] triple in centimetres, or null for a material with no
 * declared box. `volKg` is null for the same reason — a flyer has no volumetric
 * weight to be missing, which is a fact about the material rather than a gap.
 */
export const PACKS = [
  {id: 'FLY-S', label: 'Flyer, small', branding: 'self', kind: 'flyer', dims: null, selfWeightG: 11, volKg: null},
  {id: 'FLY-M', label: 'Flyer, medium', branding: 'self', kind: 'flyer', dims: null, selfWeightG: 16, volKg: null},
  {id: 'FLY-L', label: 'Flyer, large', branding: 'self', kind: 'flyer', dims: null, selfWeightG: 24, volKg: null},
  {id: 'FLY-GEN', label: 'Flyer, generic', branding: 'generic', kind: 'flyer', dims: null, selfWeightG: 14, volKg: null},
  {id: 'BOX-S', label: 'Carton, small', branding: 'self', kind: 'carton', dims: [22, 16, 8], selfWeightG: 95, volKg: 0.56},
  {id: 'BOX-M', label: 'Carton, medium', branding: 'self', kind: 'carton', dims: [30, 24, 12], selfWeightG: 165, volKg: 1.73},
  {id: 'BOX-L', label: 'Carton, large', branding: 'self', kind: 'carton', dims: [40, 32, 20], selfWeightG: 280, volKg: 5.12},
  {id: 'BOX-GEN', label: 'Carton, generic', branding: 'generic', kind: 'carton', dims: [28, 22, 14], selfWeightG: 150, volKg: 1.72},
  {id: 'PAP-W', label: 'Paper wrap', branding: 'self', kind: 'other', dims: null, selfWeightG: 30, volKg: null},
]

export function packOf(id) {
  return PACKS.find((p) => p.id === id) || null
}

/**
 * Branding rides on the option, not only on the card: two materials can share a
 * shape and differ only in whether they carry the merchant's own print, and the
 * merchant is choosing between exactly those two in the list.
 */
export function packOptionLabel(pack) {
  const brand = (PACK_BRANDING_LABEL[pack.branding] || pack.branding).toLowerCase()
  return `${pack.label} (${brand})`
}

/** A blank cell should say why it is blank. */
export function packDimsText(pack) {
  if (pack.dims) return `${pack.dims[0]} × ${pack.dims[1]} × ${pack.dims[2]} cm`
  return pack.kind === PACK_KIND.FLYER ? 'Not declared for flyers' : 'Not declared for this material'
}

export function grams(g) {
  return `${Number(g || 0).toLocaleString('en-IN')} g`
}
